import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { configuracionService } from '../services/api';
import { sanitizeHtml } from '../utils/sanitizeHtml';
import RichTextEditor from '../components/RichTextEditor';
import ConfirmPopup from '../components/ConfirmPopup';
import './Dashboard.css';
import './PlantillasMail.css';

/** Variables disponibles por tipo de plantilla (placeholder, label, descripción) */
const VARIABLES_POR_CODIGO = {
  preinscripcion_aceptada: [
    { placeholder: '{{nombre_estudiante}}', label: 'Nombre del estudiante', descripcion: 'Nombre y apellido (no se usa el usuario)' },
    { placeholder: '{{curso_nombre}}', label: 'Nombre del curso', descripcion: 'Nombre del curso al que fue aceptado' },
  ],
  preinscripcion_rechazada: [
    { placeholder: '{{nombre_estudiante}}', label: 'Nombre del estudiante', descripcion: 'Nombre y apellido (no se usa el usuario)' },
    { placeholder: '{{curso_nombre}}', label: 'Nombre del curso', descripcion: 'Nombre del curso al que no fue aceptado' },
  ],
  tarea_por_vencer: [
    { placeholder: '{{nombre_estudiante}}', label: 'Nombre del estudiante', descripcion: 'Nombre y apellido (no se usa el usuario)' },
    { placeholder: '{{curso_nombre}}', label: 'Nombre del curso', descripcion: 'Nombre del curso' },
    { placeholder: '{{tarea_titulo}}', label: 'Título de la tarea', descripcion: 'Nombre de la tarea que vence' },
    { placeholder: '{{fecha_cierre}}', label: 'Fecha de cierre', descripcion: 'Fecha límite de entrega (ej. 15/03/2025)' },
  ],
  tarea_corregida: [
    { placeholder: '{{nombre_estudiante}}', label: 'Nombre del estudiante', descripcion: 'Nombre y apellido (no se usa el usuario)' },
    { placeholder: '{{curso_nombre}}', label: 'Nombre del curso', descripcion: 'Nombre del curso' },
    { placeholder: '{{tarea_titulo}}', label: 'Título de la tarea', descripcion: 'Nombre de la tarea que fue corregida' },
  ],
  certificado_generado: [
    { placeholder: '{{nombre_estudiante}}', label: 'Nombre del estudiante', descripcion: 'Nombre y apellido (no se usa el usuario)' },
    { placeholder: '{{curso_nombre}}', label: 'Nombre del curso', descripcion: 'Nombre del curso del certificado' },
  ],
};

const VARIABLE_IMAGEN = {
  placeholder: '{{imagen_url}}',
  label: 'URL de la imagen',
  descripcion: 'Podés usar en el cuerpo algo como: <img src="{{imagen_url}}" alt="Logo" /> para mostrar la imagen subida.',
};

const PLACEHOLDERS = {
  preinscripcion_aceptada: '{{nombre_estudiante}}, {{curso_nombre}}',
  preinscripcion_rechazada: '{{nombre_estudiante}}, {{curso_nombre}}',
  tarea_por_vencer: '{{nombre_estudiante}}, {{curso_nombre}}, {{tarea_titulo}}, {{fecha_cierre}}',
  tarea_corregida: '{{nombre_estudiante}}, {{curso_nombre}}, {{tarea_titulo}}',
  certificado_generado: '{{nombre_estudiante}}, {{curso_nombre}}',
};

/** Valores de ejemplo para la vista previa del cuerpo HTML */
const PREVIEW_VALUES = {
  nombre_estudiante: 'María García',
  curso_nombre: 'Introducción a Python',
  tarea_titulo: 'Ejercicio 1 - Variables',
  fecha_cierre: '20/03/2025',
  imagen_url: '', // en preview puede quedar vacío o usar previewUrl si hay imagen
};

/** Reemplaza placeholders en el cuerpo con valores de ejemplo para la vista previa */
function cuerpoParaPreview(cuerpo, imagenPreviewUrl) {
  if (!cuerpo) return '';
  let out = cuerpo;
  for (const [key, value] of Object.entries(PREVIEW_VALUES)) {
    out = out.replace(new RegExp(`\\{\\{${key}\\}\\}`, 'g'), key === 'imagen_url' && imagenPreviewUrl ? imagenPreviewUrl : value);
  }
  if (!out.includes('<')) {
    out = out.replace(/\n/g, '<br>\n');
  }
  return out;
}

const PlantillasMail = () => {
  const navigate = useNavigate();
  const [plantillas, setPlantillas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editandoId, setEditandoId] = useState(null);
  const [form, setForm] = useState({ asunto: '', cuerpo: '', activo: true });
  const [guardando, setGuardando] = useState(false);
  const [mensaje, setMensaje] = useState({ tipo: '', texto: '' });
  const [imagenFile, setImagenFile] = useState(null);
  const [imagenPreviewUrl, setImagenPreviewUrl] = useState(null);
  const [quitarImagen, setQuitarImagen] = useState(false);
  const asuntoRef = useRef(null);
  const cuerpoEditorRef = useRef(null);
  const [cronRecordatorioActivo, setCronRecordatorioActivo] = useState(false);
  const [cronRecordatorioGuardando, setCronRecordatorioGuardando] = useState(false);

  useEffect(() => {
    loadPlantillas();
  }, []);

  useEffect(() => {
    configuracionService.getCronConfig()
      .then((list) => {
        const r = Array.isArray(list) ? list.find((c) => c.codigo === 'recordatorio_tarea_por_vencer') : null;
        setCronRecordatorioActivo(r ? r.activo : false);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!editandoId) cuerpoEditorRef.current = null;
  }, [editandoId]);

  useEffect(() => {
    if (!imagenFile) {
      if (imagenPreviewUrl) URL.revokeObjectURL(imagenPreviewUrl);
      setImagenPreviewUrl(null);
      return;
    }
    const url = URL.createObjectURL(imagenFile);
    setImagenPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [imagenFile]);

  const loadPlantillas = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await configuracionService.getPlantillasMail();
      setPlantillas(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.response?.data?.error || 'No se pudieron cargar las plantillas.');
      setPlantillas([]);
    } finally {
      setLoading(false);
    }
  };

  const abrirEditar = (p) => {
    setEditandoId(p.id);
    setForm({ asunto: p.asunto, cuerpo: p.cuerpo, activo: p.activo });
    setImagenFile(null);
    setQuitarImagen(false);
    setMensaje({ tipo: '', texto: '' });
  };

  const cerrarEditar = () => {
    setEditandoId(null);
    setForm({ asunto: '', cuerpo: '', activo: true });
    setImagenFile(null);
    setQuitarImagen(false);
  };

  const insertarVariable = (campo, placeholder) => {
    if (campo === 'cuerpo') {
      const editor = cuerpoEditorRef.current;
      if (editor) {
        editor.model.change((writer) => {
          const selection = editor.model.document.selection;
          const range = selection.getFirstRange();
          if (!range) return;
          if (!selection.isCollapsed) {
            writer.remove(range);
          }
          writer.insertText(placeholder, selection.focus);
        });
        setForm((f) => ({ ...f, cuerpo: editor.getData() }));
        setTimeout(() => {
          editor.editing.view.focus();
        }, 0);
        return;
      }
      setForm((f) => ({ ...f, cuerpo: (f.cuerpo || '') + placeholder }));
      return;
    }
    const ref = campo === 'asunto' ? asuntoRef.current : null;
    if (!ref) return;
    const start = ref.selectionStart ?? ref.value.length;
    const end = ref.selectionEnd ?? ref.value.length;
    const valor = ref.value;
    const nuevo = valor.slice(0, start) + placeholder + valor.slice(end);
    setForm((f) => ({ ...f, [campo]: nuevo }));
    setTimeout(() => {
      ref.focus();
      ref.setSelectionRange(start + placeholder.length, start + placeholder.length);
    }, 0);
  };

  const handleGuardar = async () => {
    if (editandoId == null) return;
    setGuardando(true);
    setMensaje({ tipo: '', texto: '' });
    try {
      const payload = {
        asunto: form.asunto,
        cuerpo: form.cuerpo,
        activo: form.activo,
      };
      if (quitarImagen) payload.quitar_imagen = true;
      if (imagenFile) payload.imagen = imagenFile;
      await configuracionService.updatePlantillaMail(editandoId, payload);
      setMensaje({ tipo: 'ok', texto: 'Plantilla guardada correctamente.' });
      await loadPlantillas();
      setTimeout(() => { setEditandoId(null); setMensaje({ tipo: '', texto: '' }); }, 1500);
    } catch (err) {
      setMensaje({
        tipo: 'error',
        texto: err.response?.data?.error || 'No se pudo guardar la plantilla.',
      });
    } finally {
      setGuardando(false);
    }
  };

  const plantillaEnEdicion = plantillas.find((p) => p.id === editandoId);
  const variablesParaEdicion = plantillaEnEdicion
    ? [...(VARIABLES_POR_CODIGO[plantillaEnEdicion.codigo] || []), VARIABLE_IMAGEN]
    : [];
  const imagenActualUrl = plantillaEnEdicion?.imagen_url && !quitarImagen && !imagenFile ? plantillaEnEdicion.imagen_url : null;
  const previewUrl = imagenPreviewUrl || imagenActualUrl;
  const cuerpoPreviewHtml = plantillaEnEdicion ? cuerpoParaPreview(form.cuerpo, previewUrl) : '';

  return (
    <>
      <ConfirmPopup
        open={Boolean(error) || (Boolean(mensaje.texto) && mensaje.tipo === 'error')}
        title="Error"
        message={error || mensaje.texto}
        confirmLabel="Aceptar"
        cancelLabel={null}
        onConfirm={() => { setError(''); setMensaje({ tipo: '', texto: '' }); }}
        onCancel={() => { setError(''); setMensaje({ tipo: '', texto: '' }); }}
        variant="danger"
      />
      <div className="dashboard-header plantillas-mail-header">
        <button
          type="button"
          className="plantillas-mail-back"
          onClick={() => navigate('/dashboard/configuracion')}
          aria-label="Volver a Configuración"
        >
          ← Volver a Configuración
        </button>
        <h1>Plantillas mail</h1>
        <p>Plantillas de correo para notificaciones a estudiantes.</p>
      </div>

      <section className="dashboard-section">
        {loading ? (
          <div className="dashboard-loading-cursos">
            <div className="loading-spinner"></div>
            <p>Cargando plantillas...</p>
          </div>
        ) : (
          <>
            {mensaje.texto && mensaje.tipo === 'ok' && (
              <div className="dashboard-inscripcion-msg dashboard-inscripcion-ok">
                {mensaje.texto}
              </div>
            )}
            <div className="plantillas-mail-list">
              {plantillas.map((p) => (
                <div key={p.id} className="plantilla-mail-card">
                  <div className="plantilla-mail-card-header">
                    <span className="plantilla-mail-nombre">{p.nombre}</span>
                    <span className="plantilla-mail-codigo">{p.codigo}</span>
                    {!p.activo && <span className="plantilla-mail-inactiva">Inactiva</span>}
                  </div>
                  <p className="plantilla-mail-asunto-preview"><strong>Asunto:</strong> {p.asunto}</p>
                  <p className="plantilla-mail-placeholders">
                    Variables: {PLACEHOLDERS[p.codigo] || '—'}{p.imagen_url ? ', {{imagen_url}}' : ''}
                  </p>
                  {p.codigo === 'tarea_por_vencer' && (
                    <label className="plantilla-mail-cron-check">
                      <input
                        type="checkbox"
                        checked={cronRecordatorioActivo}
                        disabled={cronRecordatorioGuardando}
                        onChange={async () => {
                          const nuevo = !cronRecordatorioActivo;
                          setCronRecordatorioGuardando(true);
                          try {
                            await configuracionService.updateCronActivo('recordatorio_tarea_por_vencer', nuevo);
                            setCronRecordatorioActivo(nuevo);
                          } catch (err) {
                            setMensaje({ tipo: 'error', texto: err.response?.data?.error || 'No se pudo actualizar.' });
                          } finally {
                            setCronRecordatorioGuardando(false);
                          }
                        }}
                      />
                      Cronjob activo (envío diario 24h antes del cierre)
                    </label>
                  )}
                  <button
                    type="button"
                    className="plantilla-mail-btn-editar"
                    onClick={() => abrirEditar(p)}
                  >
                    Editar
                  </button>
                </div>
              ))}
            </div>

            {plantillaEnEdicion && (
              <div className="plantilla-mail-modal-overlay" role="dialog" aria-modal="true" aria-labelledby="plantilla-modal-title">
                <div className="plantilla-mail-modal" onClick={(e) => e.stopPropagation()}>
                  <h2 id="plantilla-modal-title">Editar: {plantillaEnEdicion.nombre}</h2>

                  <div className="plantilla-mail-variables">
                    <h3 className="plantilla-mail-variables-title">Variables disponibles</h3>
                    <p className="plantilla-mail-variables-desc">Hacé clic en &quot;En asunto&quot; o &quot;En cuerpo&quot; para insertar la variable donde esté el cursor.</p>
                    <ul className="plantilla-mail-variables-list">
                      {variablesParaEdicion.map((v) => (
                        <li key={v.placeholder} className="plantilla-mail-variable-item">
                          <code className="plantilla-mail-variable-placeholder">{v.placeholder}</code>
                          <span className="plantilla-mail-variable-label">{v.label}</span>
                          <span className="plantilla-mail-variable-desc">{v.descripcion}</span>
                          <div className="plantilla-mail-variable-actions">
                            <button type="button" onClick={() => insertarVariable('asunto', v.placeholder)}>En asunto</button>
                            <button type="button" onClick={() => insertarVariable('cuerpo', v.placeholder)}>En cuerpo</button>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="plantilla-mail-form">
                    <label>
                      Asunto
                      <input
                        ref={asuntoRef}
                        type="text"
                        value={form.asunto}
                        onChange={(e) => setForm((f) => ({ ...f, asunto: e.target.value }))}
                        className="plantilla-mail-input"
                        maxLength={255}
                      />
                    </label>
                    <label>
                      Cuerpo (HTML)
                      <span className="plantilla-mail-label-hint">
                        Editor visual: negrita, listas, enlaces, etc. El resultado se guarda como HTML. Podés usar las variables de arriba.
                      </span>
                      <div className="plantilla-mail-editor-wrap">
                        <RichTextEditor
                          key={editandoId}
                          value={form.cuerpo}
                          onChange={(html) => setForm((f) => ({ ...f, cuerpo: html }))}
                          placeholder="Ej.: párrafo de saludo, recordatorio del curso…"
                          minHeight="220px"
                          onEditorReady={(editor) => {
                            cuerpoEditorRef.current = editor;
                          }}
                        />
                      </div>
                    </label>
                    <div className="plantilla-mail-preview-wrap">
                      <h3 className="plantilla-mail-preview-title">Vista previa</h3>
                      <div
                        className="plantilla-mail-preview"
                        dangerouslySetInnerHTML={{
                          __html: sanitizeHtml(
                            cuerpoPreviewHtml || '<span class="plantilla-mail-preview-empty">El cuerpo está vacío</span>'
                          ),
                        }}
                      />
                    </div>
                  </div>

                  <div className="plantilla-mail-imagen-section">
                    <h3 className="plantilla-mail-imagen-title">Imagen para el mail</h3>
                    <p className="plantilla-mail-imagen-desc">
                      Opcional. Si subís una imagen, en el cuerpo podés usar <code>{'{{imagen_url}}'}</code> (por ejemplo en HTML: <code>{'<img src="{{imagen_url}}" alt="Logo" />'}</code>).
                    </p>
                    {previewUrl && (
                      <div className="plantilla-mail-imagen-preview">
                        <img src={previewUrl} alt="Vista previa" />
                      </div>
                    )}
                    <div className="plantilla-mail-imagen-actions">
                      <label className="plantilla-mail-imagen-upload">
                        <span className="plantilla-mail-imagen-upload-btn">Elegir imagen</span>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            e.target.value = '';
                            if (file) {
                              setImagenFile(file);
                              setQuitarImagen(false);
                            }
                          }}
                        />
                      </label>
                      {(imagenActualUrl || imagenFile) && (
                        <button
                          type="button"
                          className="plantilla-mail-imagen-quitar"
                          onClick={() => {
                            setImagenFile(null);
                            setQuitarImagen(true);
                          }}
                        >
                          Quitar imagen
                        </button>
                      )}
                    </div>
                  </div>

                  <label className="plantilla-mail-check">
                    <input
                      type="checkbox"
                      checked={form.activo}
                      onChange={(e) => setForm((f) => ({ ...f, activo: e.target.checked }))}
                    />
                    Plantilla activa (se envían mails con esta plantilla)
                  </label>
                  <div className="plantilla-mail-modal-actions">
                    <button type="button" className="plantilla-mail-btn-cancelar" onClick={cerrarEditar}>
                      Cancelar
                    </button>
                    <button
                      type="button"
                      className="plantilla-mail-btn-guardar"
                      onClick={handleGuardar}
                      disabled={guardando}
                    >
                      {guardando ? 'Guardando...' : 'Guardar'}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </section>
    </>
  );
};

export default PlantillasMail;
